import { useEffect, useRef, useState } from 'react';
import { isNil } from 'lodash';

import type { AiAction } from '~/components/AiChat/types';
import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';
import type { ClaimActionsListResponse } from '~/server_shared/openapi/data-contracts';

import { getNotifiedActionId } from '../utils';

/**
 * Refetches the claim's actions when an action entry names a row the tab has not read.
 * claims-ai saves the row before publishing the entry, so a read sent after the entry arrived
 * finds it.
 *
 * An id is marked checked only once a read this hook sent *for it* has succeeded — never off rows
 * from a read that may have left before the entry arrived. That includes the first read: it usually
 * lands well before the stream, and an action created in between is in the first snapshot but not
 * in the rows. One refetch at a time: an id that arrives while one is in flight waits for it to
 * land and gets its own. A read that fails marks nothing and sends nothing more, since retrying off
 * its own failure would loop against a route that is down: its ids, and any that arrived while it
 * was in flight, are asked for again when either source next changes.
 */
export const useRefetchForUnreadActions = (
  notifications: StpNotification[],
  /** The rows as last read. */
  actions: AiAction[],
  /** Both sources have made their first read (the rows usually land before the stream). */
  isReady: boolean,
  /**
   * `reloadData`: resolves to the response, or to `undefined` when the read failed; it never
   * rejects. Typed to the response so a callback that resolves to `void`, such as
   * `handleActionUpdate`, does not compile: every read would look failed, and an id the server
   * never has would be read for again after each one.
   */
  refetchActions: () => Promise<ClaimActionsListResponse | undefined>
): void => {
  const checkedIds = useRef(new Set<string>());
  const isRefetching = useRef(false);
  // Bumped when a refetch succeeds, so ids that arrived while it was in flight get theirs.
  const [landedCount, setLandedCount] = useState(0);

  useEffect(() => {
    if (!isReady || isRefetching.current) return;

    const readIds = new Set(actions.map((action) => action.action_id));
    const missingIds = notifications
      .map(getNotifiedActionId)
      .filter((actionId): actionId is string => !isNil(actionId) && !readIds.has(actionId));

    const checked = checkedIds.current;
    const requestedIds = missingIds.filter((actionId) => !checked.has(actionId));
    if (!requestedIds.length) return;

    isRefetching.current = true;
    refetchActions().then((response) => {
      isRefetching.current = false;
      if (isNil(response)) return;

      requestedIds.forEach((actionId) => checked.add(actionId));
      setLandedCount((count) => count + 1);
    });
  }, [notifications, actions, isReady, refetchActions, landedCount]);
};
